import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE, HERO_IMAGE_ALT } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const negatives = ['Not a random collection of nursing videos.', 'Not a pile of old lecture slides.', 'Not a text-heavy textbook you have to turn into classes.', 'Not a pile of resources you have to organize yourself.'];
const teachingFlow = ['OPEN THE SYSTEM.', 'CHOOSE THE TOPIC.', 'SHOW THE VISUAL.', 'TEACH THE SESSION.', 'RUN THE PRACTICE.', 'CHECK COMPETENCY.'];
const teachingAreas = [{
  number: '01',
  step: 'NURSING FUNDAMENTALS, PATIENT CARE & ESSENTIAL CLINICAL SKILLS',
  detail: 'Nursing process, hand hygiene, PPE, vital signs, safe patient handling, basic care and documentation.'
}, {
  number: '02',
  step: 'HUMAN ANATOMY, HEALTH ASSESSMENT & CLINICAL EXAMINATION',
  detail: 'Body systems, health history, exam techniques, head-to-toe assessment and abnormal findings.'
}, {
  number: '03',
  step: 'NURSING PROCEDURES, CLINICAL INTERVENTIONS & PATIENT SAFETY',
  detail: 'Sterile technique, wound care, catheters, oxygen therapy, IV fundamentals and infection prevention.'
}, {
  number: '04',
  step: 'NURSING PHARMACOLOGY, MEDICATION ADMINISTRATION & DOSAGE CALCULATIONS',
  detail: 'Medication rights, routes, injections, medication classes, dosage formulas and IV rates.'
}, {
  number: '05',
  step: 'MEDICAL-SURGICAL NURSING, EMERGENCY CARE & CLINICAL DECISION-MAKING',
  detail: 'Prioritization, deterioration, common med-surg conditions, perioperative care and emergency response.'
}, {
  number: '06',
  step: 'NURSING INSTRUCTION, CLINICAL SIMULATION & COMPETENCY ASSESSMENTS',
  detail: 'Lesson design, skills demonstrations, simulation scenarios, debriefing, checklists and rubrics.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-[#F3F7FA] relative overflow-hidden" data-id="element-282" data-mp="2hpe8zofr5mge">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-[#0A7D79]" data-id="element-283" data-mp="2cr2vn3q90ttf" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-284" data-mp="2qi94hqff43sz">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-285" data-mp="2mhxjgcj581gz">
          <p className="text-sm md:text-base font-semibold text-[#025B57] mb-4" data-id="element-286" data-mp="1tt9y6gye9c3f">
            The Complete Visual Nursing Training System for Nursing Instructors, Clinical Faculty &amp; Nurse Educators
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-[#0B2545]" data-id="element-287" data-mp="22tgfkdx7qw7v">
            NURSING INSTRUCTOR <span className="text-[#0A7D79]" data-id="element-288" data-mp="3j76nxwvd86nb">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-[#0B2545] font-semibold mb-6" data-id="element-289" data-mp="3e9dfex6lb0jv">
            A Complete 6-IN-1 Visual Nursing Education &amp; Clinical Training System
          </p>
          <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} className="w-full max-w-sm h-auto mx-auto mb-6 drop-shadow-xl" loading="lazy" data-id="element-290" data-mp="3msi3g9z5ov2n" />
          <p className="text-base md:text-lg text-[#025B57] font-semibold mb-10" data-id="element-291" data-mp="3cuary6nmz581">
            6 PROFESSIONAL VOLUMES · 300 VISUAL TRAINING PAGES · 5 EXCLUSIVE BONUSES
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-292" data-mp="9dljgf4htlfq">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-slate-200" data-id="element-293" data-mp="e90xb72i3hc5">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-294" data-mp="x0tpiz9qwyit">
                <X className="text-[#0B2545]/50 shrink-0 mt-1" size={18} data-id="element-295" data-mp="x24siwvda28v" />
                <p className="text-slate-700/80 text-sm" data-id="element-296" data-mp="34l6dqw1veyhh">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-[#0B2545]" data-id="element-297" data-mp="30kw6zh69841k">
            <div className="flex items-start gap-3" data-id="element-298" data-mp="3o7ktyha4emp1">
              <Check className="text-[#0A7D79] shrink-0 mt-1" size={20} data-id="element-299" data-mp="3j8rgupyqwxpe" />
              <div className="space-y-3" data-id="element-300" data-mp="3eiksmr85bf7o">
                <p className="text-[#0B2545] font-medium text-base leading-relaxed" data-id="element-301" data-mp="3ehj5vphrp4a4">
                  NURSING INSTRUCTOR ATLAS is a complete visual training system for nursing instructors, clinical
                  faculty, nurse educators and skills-lab and simulation coordinators — from beginner to
                  advanced.
                </p>
                <p className="text-[#025B57] font-semibold text-sm leading-relaxed" data-id="element-302" data-mp="60y6n0extpk7">
                  Every page gives you an illustration, labeled diagram or clinical sequence, a step-by-step
                  breakdown, a ❌ common mistake vs. ✅ correct technique comparison, a teaching cue, a drill,
                  a quick check and an Easy / Standard / Advanced variant — all in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-303" data-mp="wap069u7nbj0">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-304" data-mp="11bcrkwmcpudr">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#0B2545]" data-id="element-305" data-mp="16337di425ack">
                {line}
              </li>)}
          </ul>
          <p className="mt-8 text-lg md:text-xl font-bold text-[#0B2545]" data-id="element-306" data-mp="1g1k3ethusf5h">
            Make Your Next Nursing Session Easier to Prepare and More Visual to Teach.
          </p>
          <div className="flex justify-center mt-5" data-id="element-307" data-mp="3h2qlpg8mrz1j">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg py-3 sm:py-4 px-5 sm:px-8" data-id="element-308" data-mp="3h3i9i4vzksh9">
              GET MY COMPLETE 6-IN-1 NURSING TRAINING SYSTEM NOW!
            </Button>
          </div>
        </div>

        <div className="mt-12" data-id="element-309" data-mp="3l30t8xc2mxii">
          <p className="text-[#025B57] text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-310" data-mp="3o3q54jd6oi7">
            The System Covers Six Essential Nursing Teaching Areas
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-311" data-mp="3uu57tvxu4gct">
            {teachingAreas.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-slate-200" data-id="element-312" data-mp="8n6wcsb2z0rr">
                <span className="text-sm font-bold text-[#025B57] mb-1" data-id="element-313" data-mp="2ei7vyzgl09rn">{item.number}</span>
                <span className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-[#0B2545]" data-id="element-314" data-mp="2agcwm0i7o6gi">
                  {item.step}
                </span>
                <span className="text-sm text-slate-700/80 mt-1" data-id="element-315" data-mp="24b5aibzrun89">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-[#0B2545]" data-id="element-316" data-mp="1qkjq2iuo6589">
            SIX AREAS. SIX COMPLETE VOLUMES. ONE PROFESSIONAL TRAINING SYSTEM.
          </p>
        </div>
      </div>
    </section>;
}